import React, { LegacyRef, useEffect, useState, useRef } from 'react';
import { QuestionProps } from '@de/components/Step/Step';
import { Option } from '@de/types/types';
import { useDynamicExperienceContext } from '@de/providers/DynamicExperienceProvider';
import useQuestionEvents from '@de/hooks/useQuestionEvents';
import { useOfferingsFilterOptions } from '@de/hooks/useOfferingsFilterOptions';
import { ErrorMessage } from '@de/components/ErrorMessage';

const DynamicSingleChoiceQuestion: React.FC<QuestionProps> = ({
  options = [],
  id = '',
  leadFieldAssociated,
  automaticNext = false,
  isMultiQuestionChild = false,
  title,
  stepName,
  dynamicOptionsFilters,
  variant,
  isOptionsLoading = false,
  dynamicOptionsError,
}) => {
  const { updateFlowState, getStepMetadataById, handleStepForward } =
    useDynamicExperienceContext();
  const stepSelectedData = getStepMetadataById(id || '');
  const selectedOption = stepSelectedData?.option as Option;
  const [searchText, setSearchText] = useState<string>('');
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const [, setDropdownSelectedOptions] = useState<Option[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { dynamicOptions, isLoading, fetchError } = useOfferingsFilterOptions(
    dynamicOptionsFilters,
  );

  const { ref: visibilityRef, triggerFieldInputted } = useQuestionEvents({
    id,
    fieldType: 'single_choice',
    options,
    title,
    stepName,
    isMultiQuestionChild,
    variant: variant || 'dynamic_single_choice',
    leadFieldAssociated,
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const allOptions = dynamicOptions;

  const getDisplayedOptions = () => {
    const first10 = allOptions.slice(0, 10);
    const first10Ids = new Set(first10.map((opt) => opt.optionId));

    if (selectedOption && !first10Ids.has(selectedOption.optionId)) {
      return [selectedOption, ...first10];
    }

    return first10;
  };

  const displayedOptions = getDisplayedOptions();

  const getFilteredDropdownOptions = () => {
    if (!searchText || allOptions.length <= 10) return [];

    const displayedIds = new Set(displayedOptions.map((opt) => opt.optionId));

    return allOptions.filter(
      (option) =>
        !displayedIds.has(option.optionId) &&
        (option.text as string)
          .toLowerCase()
          .includes(searchText.toLowerCase()),
    );
  };

  const filteredDropdownOptions = getFilteredDropdownOptions();

  const handleDropdownSelect = (option: Option) => {
    setDropdownSelectedOptions((prev) => {
      const filtered = prev.filter((opt) => opt.optionId !== option.optionId);
      return [option, ...filtered];
    });

    updateFlowState(id, { option, leadFieldAssociated });

    triggerFieldInputted(option.text as string);

    setSearchText('');
    setShowDropdown(false);

    if (automaticNext && !isMultiQuestionChild) {
      handleStepForward();
    }
  };

  const effectiveError = dynamicOptionsError ?? fetchError;
  const showOptionsSkeleton =
    (isLoading || isOptionsLoading) && !effectiveError;
  const isEmpty =
    !isLoading &&
    !isOptionsLoading &&
    !effectiveError &&
    allOptions.length === 0;

  return (
    <>
      {effectiveError ? (
        <ErrorMessage
          message={effectiveError}
          className='w-full justify-center py-2 px-2'
        />
      ) : null}
      {isEmpty ? (
        <p
          role='status'
          className='w-full text-center text-sm text-gray-500 py-2 px-2'
        >
          No options found
        </p>
      ) : null}
      <div
        className='flex flex-wrap gap-4 py-6 font-normal w-full items-center justify-center'
        ref={visibilityRef as LegacyRef<HTMLDivElement>}
      >
        {showOptionsSkeleton
          ? Array.from({ length: 10 }).map((_, index) => (
              <div
                key={index}
                className='animate-pulse rounded-full bg-gray-300 h-10'
                style={{ width: `${120 + (index % 3) * 40}px` }}
              />
            ))
          : displayedOptions.map((option, index) => {
              const isSelected =
                selectedOption &&
                !Array.isArray(selectedOption) &&
                selectedOption.optionId === option.optionId;
              return (
                <button
                  key={index}
                  className={`mx-0 transition-all duration-200 ease-in-out mb-0 flex w-fit flex-initial cursor-pointer items-center justify-center gap-x-2 rounded-full border-2 px-5 py-2 text-sm hover:border-gray-300 hover:bg-gray-100 ${
                    isSelected
                      ? 'bg-secondary-800 text-white border-secondary-700'
                      : ''
                  }`}
                  onClick={() => {
                    if (isSelected) {
                      updateFlowState(id, null);
                      setDropdownSelectedOptions((prev) =>
                        prev.filter((opt) => opt.optionId !== option.optionId),
                      );
                    } else {
                      updateFlowState(id, { option, leadFieldAssociated });
                      if (automaticNext && !isMultiQuestionChild) {
                        handleStepForward();
                      }
                    }

                    triggerFieldInputted(option.text as string);
                  }}
                >
                  <svg
                    className={`w-[14px] h-[14px] ${isSelected ? 'block' : 'hidden'}`}
                    viewBox='0 0 20 20'
                    fill='currentColor'
                  >
                    <path
                      fillRule='evenodd'
                      d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                      clipRule='evenodd'
                    />
                  </svg>
                  {option.text}
                </button>
              );
            })}
      </div>

      {showOptionsSkeleton && (
        <div className='w-full relative mb-6'>
          <div className='animate-pulse rounded-lg bg-gray-300 h-11 w-full' />
        </div>
      )}

      {!showOptionsSkeleton && allOptions.length > 10 && (
        <div className='w-full relative mb-6' ref={dropdownRef}>
          <input
            type='text'
            placeholder='Search options...'
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setShowDropdown(e.target.value.length > 0);
            }}
            onFocus={() => searchText.length > 0 && setShowDropdown(true)}
            className='w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
          />
          {showDropdown && filteredDropdownOptions.length > 0 && (
            <div className='absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-xl shadow-lg max-h-60 overflow-y-auto'>
              {filteredDropdownOptions.map((option, index) => (
                <button
                  key={index}
                  onClick={() => handleDropdownSelect(option)}
                  className='w-full px-4 py-3 text-left hover:bg-gray-100 transition-colors border-b border-gray-200 last:border-b-0 focus:outline-none focus:bg-gray-100 no-underline'
                >
                  <div className='font-medium text-gray-900'>{option.text}</div>
                </button>
              ))}
            </div>
          )}
          {showDropdown && filteredDropdownOptions.length === 0 && (
            <div className='absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-xl shadow-lg px-4 py-3 text-gray-500'>
              No options found
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default DynamicSingleChoiceQuestion;
